import React, { Dispatch, SetStateAction, useMemo } from "react"

import { Resizable } from "re-resizable"

import { Subscription } from "@rails/actioncable"

import useWebsocket from "hooks/useWebsocket"

import CaptionView from "components/AudioRoom/CaptionView"
import MuteButton from "components/AudioRoom/MuteButton"
import RosterSidebar from "components/AudioRoom/RosterSidebar"
import { ChimeJoinInfo, HandleAudioRoomError } from "components/AudioRoom/types"
import useAudioRoom from "components/AudioRoom/useAudioRoom"
import useGeminiLiveTranslation from "components/ChimeMeetingHooks/useGeminiLiveTranslation"
import useTranscriptForwarding from "components/ChimeMeetingHooks/useTranscriptForwarding"

import TeacherToolsPanel from "./TeacherToolsPanel"

type TeacherRoomProps = {
  joinInfo: ChimeJoinInfo
  handleError: HandleAudioRoomError
  setUserError: Dispatch<SetStateAction<string | undefined>>
  audioRoomId: string
  currentUserId: string
  audioInputDeviceId: string
  audioInputDeviceName: string
  controlsUnlockedUserIds: string[]
}

const TeacherRoom: React.FC<TeacherRoomProps> = ({
  joinInfo,
  handleError,
  setUserError,
  audioRoomId,
  currentUserId,
  audioInputDeviceId,
  audioInputDeviceName,
  controlsUnlockedUserIds: initialControlsUnlockedUserIds,
}) => {
  const { connected, muted, mute, unmute, roster, audioVideo } = useAudioRoom(
    joinInfo,
    handleError,
    setUserError,
    audioRoomId,
    currentUserId,
    audioInputDeviceId
  )
  const channelProps = useMemo(() => ({ id: audioRoomId }), [audioRoomId])
  const { channel }: { channel: Subscription } = useWebsocket({
    channelName: "AudioRoomChannel",
    channelProps,
  })
  useWebsocket({
    channelName: "AudioRoomUserChannel",
    channelProps,
  })

  useTranscriptForwarding({
    enabled: true,
    audioVideo,
    userId: String(currentUserId),
    channel,
  })

  const translatingUserPresent = Object.values(roster).some(
    user =>
      user.attendeeId !== joinInfo.attendee.attendeeId &&
      user.defaultLiveTranslationLanguageCode
  )

  useGeminiLiveTranslation({
    audioVideo,
    targetLanguageCode: translatingUserPresent ? "en" : undefined,
    chimeMeetingId: joinInfo.meeting.meeting_id,
  })

  const presentStudents = useMemo(() => {
    const students = new Map<string, { fullName: string; id: string }>()
    Object.values(roster).forEach(user => {
      if (user.userType === "Student" && user.userId && user.fullName) {
        students.set(user.userId, { id: user.userId, fullName: user.fullName })
      }
    })
    return Array.from(students.values())
  }, [roster])

  return (
    <>
      <audio className="hidden" id="room-audio" />
      <div className="grid h-screen grid-cols-[auto_minmax(0,1fr)]">
        <RosterSidebar
          audioRoomId={audioRoomId}
          roster={roster}
          localAttendeeId={joinInfo.attendee.attendeeId}
          connected={connected}
          channel={channel}
          showInviteForm
          initialControlsUnlockedUserIds={initialControlsUnlockedUserIds}
        />
        <main className="flex min-h-0 flex-col">
          <TeacherToolsPanel
            audioRoomId={audioRoomId}
            presentStudents={presentStudents}
          />
          <Resizable
            defaultSize={{ width: "100%", height: "50%" }}
            minHeight={160}
            maxHeight="80%"
            enable={{ top: true }}
            handleClasses={{
              top: "z-10 h-2 cursor-row-resize hover:bg-white/20 focus:bg-white/20",
            }}
            className="relative flex-none border-t-4 border-solid border-neutral-700"
          >
            <CaptionView audioVideo={audioVideo} roster={roster} />
            <MuteButton
              connected={connected}
              muted={muted}
              toggleMute={() => (muted ? unmute : mute)()}
              microphoneName={audioInputDeviceName}
            />
          </Resizable>
        </main>
      </div>
    </>
  )
}

export default TeacherRoom
